import { useCallback, useEffect, useRef } from "react";

import {
  getDialoguePlaybackObjectKey,
  getDialoguePlaybackRefreshDelay,
  hydrateDialoguePlaybackUrls,
  isSignedMediaUrl,
  PLAYBACK_REFRESH_RETRY_MS,
} from "../utils/audioMedia";

// languageCode limits refreshes to the audio the page plays (see
// hydrateDialoguePlaybackUrls).
export const useDialoguePlaybackRefresh = ({
  dialogues,
  projectId,
  setDialogues,
  languageCode,
}) => {
  const dialoguesRef = useRef(dialogues);
  const refreshInFlightRef = useRef(null);
  const retriedMediaUrlsRef = useRef(new WeakMap());
  const playbackObjectKey = getDialoguePlaybackObjectKey(
    dialogues,
    projectId,
    { languageCode }
  );

  useEffect(() => {
    dialoguesRef.current = dialogues;
  }, [dialogues]);

  const refreshPlaybackUrls = useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (!projectId || !dialoguesRef.current?.length) {
        return null;
      }
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }

      const refresh = hydrateDialoguePlaybackUrls(
        dialoguesRef.current,
        projectId,
        { forceRefresh, languageCode }
      )
        .then((refreshedDialogues) => {
          dialoguesRef.current = refreshedDialogues;
          setDialogues(refreshedDialogues);
          return refreshedDialogues;
        })
        .finally(() => {
          refreshInFlightRef.current = null;
        });

      refreshInFlightRef.current = refresh;
      return refresh;
    },
    [languageCode, projectId, setDialogues]
  );

  useEffect(() => {
    if (!projectId || !dialogues.length) {
      return undefined;
    }

    let cancelled = false;
    let refreshTimer;

    const scheduleRefresh = (minimumDelay = 0) => {
      if (cancelled) {
        return;
      }
      const delay = getDialoguePlaybackRefreshDelay(
        dialoguesRef.current,
        projectId,
        { languageCode }
      );
      if (delay === null) {
        return;
      }

      refreshTimer = window.setTimeout(async () => {
        await refreshPlaybackUrls({ forceRefresh: true });
        if (!cancelled) {
          scheduleRefresh(PLAYBACK_REFRESH_RETRY_MS);
        }
      }, Math.max(delay, minimumDelay));
    };

    scheduleRefresh();
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer);
    };
  }, [
    dialogues.length,
    languageCode,
    playbackObjectKey,
    projectId,
    refreshPlaybackUrls,
  ]);

  useEffect(() => {
    const retryFailedSignedMedia = (event) => {
      const mediaElement = event.target;
      if (!mediaElement || !["AUDIO", "VIDEO"].includes(mediaElement.tagName)) {
        return;
      }

      const failedUrl = mediaElement.currentSrc || mediaElement.src;
      if (
        !isSignedMediaUrl(failedUrl) ||
        retriedMediaUrlsRef.current.get(mediaElement) === failedUrl
      ) {
        return;
      }

      retriedMediaUrlsRef.current.set(mediaElement, failedUrl);
      refreshPlaybackUrls({ forceRefresh: true });
    };

    window.addEventListener("error", retryFailedSignedMedia, true);
    return () => {
      window.removeEventListener("error", retryFailedSignedMedia, true);
    };
  }, [refreshPlaybackUrls]);

  return refreshPlaybackUrls;
};
